"use client";

import React, { useState, useEffect } from "react";
import { Button } from "@/components/actions/Button";
import { appendUtmsToUrl, sendGAEvent } from "@/lib/analytics";
import Script from "next/script";

export const ContactForm: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [formCaptureReady, setFormCaptureReady] = useState(false);
  const [submissionState, setSubmissionState] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    message: string;
  }>({
    status: 'idle',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length <= 3) {
      return phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  useEffect(() => {
    if (!formCaptureReady) return;

    const initFormCapture = () => {
      const d365 = (window as any).d365mktformcapture;
      if (!d365) return;

      d365.waitForElement("#contact-form-cd3baf9b")
        .then((form: HTMLFormElement) => {
          const mappings = [
            { FormFieldName: "firstname", DataverseFieldName: "firstname" },
            { FormFieldName: "lastname", DataverseFieldName: "lastname" },
            { FormFieldName: "companyname", DataverseFieldName: "companyname" },
            { FormFieldName: "jobtitle", DataverseFieldName: "jobtitle" },
            { FormFieldName: "emailaddress1", DataverseFieldName: "emailaddress1" },
            { FormFieldName: "telephone1", DataverseFieldName: "telephone1" },
            { FormFieldName: "consentCheckbox", DataverseFieldName: "consentCheckbox" },
            { FormFieldName: "mc_leadsource", DataverseFieldName: "mc_leadsource" },
            { FormFieldName: "mc_url", DataverseFieldName: "mc_url" },
          ];

          // Define the submit handler function
          let formSubmitHandler: ((e: Event) => Promise<void>) | null = null;

          formSubmitHandler = async (e: Event) => {
            e.preventDefault();
            e.stopPropagation();

            // Prevent double submission
            if (isSubmitting) return;
            setIsSubmitting(true);

            // Set loading state
            setSubmissionState({ status: 'loading', message: '' });

            // Update hidden URL field with UTMs before serialization
            const urlField = form.querySelector('input[name="mc_url"]') as HTMLInputElement;
            if (urlField && typeof window !== "undefined") {
              urlField.value = appendUtmsToUrl(window.location.href);
            }

            // Clean phone number before submission
            const phoneField = form.querySelector('input[name="telephone1"]') as HTMLInputElement;
            if (phoneField) {
              phoneField.value = phoneField.value.replace(/\D/g, "");
            }

            // Serialize form using FormCapture
            const serializedForm = d365.serializeForm(form, mappings);
            const builtPayload = serializedForm.SerializedForm.build();

            // The build() method wraps the actual payload in { contentType, data }
            // where data is a stringified JSON, so we need to parse it
            const payload = JSON.parse(builtPayload.data);

            // Submit through our API route to avoid CORS
            try {
              const response = await fetch("/api/submit-form", {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  formId: "cd3baf9b-ad98-f011-b4cc-000d3a5a7d86",
                  captureData: payload,
                }),
              });

              const result = await response.json();

              if (response.ok && result.success) {
                // Track successful submission
                sendGAEvent("form_submit", "contact_form", "submit");

                // Set success state
                setSubmissionState({
                  status: 'success',
                  message: "Thank you! Your message has been sent successfully. We'll get back to you soon."
                });

                // Reset form after successful submission
                form.reset();
                setPhone("");
              } else {
                console.error("Form submission failed:", result);
                setSubmissionState({
                  status: 'error',
                  message: result.error || "Sorry, there was an error submitting the form. Please try again."
                });
              }
            } catch (error) {
              console.error("Form submission error:", error);
              setSubmissionState({
                status: 'error',
                message: "Network error occurred. Please check your connection and try again."
              });
            } finally {
              // Reset submitting flag after completion
              setIsSubmitting(false);
            }
          };

          // Add the submit event listener
          if (formSubmitHandler) {
            form.addEventListener("submit", formSubmitHandler, true);
          }
        })
        .catch((error: Error) => {
          console.error("Form capture initialization error:", error);
        });
    };

    initFormCapture();
  }, [formCaptureReady]);

  return (
    <>
      <form id="contact-form-cd3baf9b" className="stack gap-6">
        <div className="stack gap-4">
        <div className="stack gap-2">
          <label htmlFor="firstname-contact" className="font-semibold">
            First Name <span className="text-red-600">*</span>
          </label>
          <input
            id="firstname-contact"
            name="firstname"
            type="text"
            placeholder="Enter your first name"
            required
            maxLength={50}
            className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>

        <div className="stack gap-2">
          <label htmlFor="lastname-contact" className="font-semibold">
            Last Name <span className="text-red-600">*</span>
          </label>
          <input
            id="lastname-contact"
            name="lastname"
            type="text"
            placeholder="Enter your last name"
            required
            maxLength={50}
            className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>

        <div className="stack gap-2">
          <label htmlFor="companyname-contact" className="font-semibold">
            Company Name <span className="text-red-600">*</span>
          </label>
          <input
            id="companyname-contact"
            name="companyname"
            type="text"
            placeholder="Enter your company name"
            required
            maxLength={100}
            className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>

        <div className="stack gap-2">
          <label htmlFor="jobtitle-contact" className="font-semibold">
            Job Title <span className="text-red-600">*</span>
          </label>
          <input
            id="jobtitle-contact"
            name="jobtitle"
            type="text"
            placeholder="Job Title"
            required
            maxLength={100}
            className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>

        <div className="stack gap-2">
          <label htmlFor="emailaddress1-contact" className="font-semibold">
            Email <span className="text-red-600">*</span>
          </label>
          <input
            id="emailaddress1-contact"
            name="emailaddress1"
            type="email"
            placeholder="Enter your work email address"
            required
            className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
          />
        </div>

        <div className="stack gap-2">
          <label htmlFor="telephone1-contact" className="font-semibold">
            Phone Number
          </label>
          <input
            id="telephone1-contact"
            name="telephone1"
            type="tel"
            placeholder="(555) 555-5555"
            value={phone}
            onChange={handlePhoneChange}
            pattern="^\(\d{3}\)\s\d{3}-\d{4}$"
            title="Please enter a 10-digit phone number"
            className="w-full border border-gold rounded px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-gold"
          />
          
        </div>
      </div>

      <input
        type="hidden"
        id="mc_leadsource-contact"
        name="mc_leadsource"
        value="948100080"
      />
        <input
          type="hidden"
          id="mc_url-contact"
          name="mc_url"
        />

        
                      <div className="flex items-center gap-4">
                        <input
                          type="checkbox"
                          id="consentCheckbox-1759410030253"
                          name="consentCheckbox"
                          required
                          className="w-5 h-5 border-2 border-gold rounded bg-white text-gold focus:ring-2 focus:ring-gold"
                        />
                        <label htmlFor="consentCheckbox-1759410030253" className="text-sm">
                          I agree to receive training and CEU opportunities, product updates, and relevant communications from Forte Opening Solutions.
                        </label>
                      </div>

                      {/* Messages positioned closer to the button */}
                      {submissionState.status === 'success' && (
                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-green-800 font-medium">Success!</p>
                              <p className="text-green-700 text-sm">{submissionState.message}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {submissionState.status === 'error' && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-3">
                            <svg className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div className="flex-1">
                              <p className="text-red-800 font-medium">Error</p>
                              <p className="text-red-700 text-sm">{submissionState.message}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="flex justify-start">
                        <Button
                          type="submit"
                          disabled={submissionState.status === 'loading' || isSubmitting}
                          className="relative"
                        >
                          {submissionState.status === 'loading' ? (
                            <>
                              <svg className="inline-block animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Submitting...
                            </>
                          ) : (
                            'Submit'
                          )}
                        </Button>
                      </div>
      </form>
      <Script
        src="https://cxppusa1formui01cdnsa01-endpoint.azureedge.net/usa/FormCapture/FormCapture.bundle.js"
        strategy="lazyOnload"
        onReady={() => setFormCaptureReady(true)}
      />
    </>
  );
};
